// Microphone capture with a simple energy VAD (live "parrot" mode) or push-to-record.
const resample = async (data, sr, to = 44100) => {
  if (sr === to) return data;
  const off = new OfflineAudioContext(1, Math.ceil((data.length * to) / sr), to);
  const buf = off.createBuffer(1, data.length, sr); buf.copyToChannel(data, 0);
  const src = off.createBufferSource(); src.buffer = buf; src.connect(off.destination); src.start();
  return (await off.startRendering()).getChannelData(0).slice();
};

// The mic stream stays warm between uses (instant Record/Stop) and is released after a short idle time.
const IDLE_MS = 20000, DEV_KEY = 'vf_mic_device';
let held = null, idleT = 0;
export const getMicDevice = () => localStorage.getItem(DEV_KEY) || '';
export const releaseMic = () => {
  clearTimeout(idleT);
  if (!held) return;
  held.stream.getTracks().forEach((t) => t.stop()); held.ctx.close().catch(() => {}); held = null;
};
export const setMicDevice = (id) => { localStorage.setItem(DEV_KEY, id); releaseMic(); };
export const listMics = async () => (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput' && d.deviceId);
export const activeMicLabel = () => held?.stream.getAudioTracks()[0]?.label || '';

export const acquireMic = async () => {
  clearTimeout(idleT);
  if (held && held.stream.active) { if (held.ctx.state !== 'running') await held.ctx.resume(); return held; }
  releaseMic();
  const id = getMicDevice();
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { ...(id ? { deviceId: { ideal: id } } : {}), echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
  const ctx = new AudioContext();
  held = { stream, ctx, src: ctx.createMediaStreamSource(stream) };
  return held;
};
export const idleMic = () => { clearTimeout(idleT); idleT = setTimeout(releaseMic, IDLE_MS); };
// The lecture ToolsDock pauses background tools (iframes) with this event.
if (typeof window !== 'undefined') window.addEventListener('vf-audio', (e) => { if (!e.detail) releaseMic(); });

export const startMic = async ({ vad = true, onLevel, onUtterance, onSpeech, maxSec = 10 }) => {
  const { ctx, src } = await acquireMic();
  const proc = ctx.createScriptProcessor(2048, 1, 1);
  const sr = ctx.sampleRate, pre = Math.round(0.25 * sr);
  let ring = [], ringLen = 0, chunks = [], len = 0, speaking = false, quiet = 0, loud = 0, paused = false;
  const flush = async () => {
    const out = new Float32Array(len); let o = 0; chunks.forEach((c) => { out.set(c, o); o += c.length; });
    chunks = []; len = 0; speaking = false; quiet = 0; loud = 0;
    if (out.length > 0.25 * sr) onUtterance?.(await resample(out, sr));
  };
  proc.onaudioprocess = (e) => {
    const x = new Float32Array(e.inputBuffer.getChannelData(0));
    let s = 0; for (let i = 0; i < x.length; i++) s += x[i] * x[i];
    const rms = Math.sqrt(s / x.length);
    onLevel?.(rms);
    if (paused) return;
    if (!vad) { chunks.push(x); len += x.length; return; }
    if (!speaking) {
      ring.push(x); ringLen += x.length;
      while (ringLen - ring[0].length > pre) ringLen -= ring.shift().length;
      loud = rms > 0.02 ? loud + 1 : 0;
      if (loud >= 2) { speaking = true; chunks = ring; len = ringLen; ring = []; ringLen = 0; onSpeech?.(true); }
      return;
    }
    chunks.push(x); len += x.length;
    quiet = rms < 0.012 ? quiet + x.length : 0;
    if (quiet > 0.7 * sr || len > maxSec * sr) { onSpeech?.(false); flush(); }
  };
  src.connect(proc); proc.connect(ctx.destination);
  let stopped = false;
  return {
    pause: (p) => { paused = p; if (p) { chunks = []; len = 0; speaking = false; } },
    // Grab the recorded audio and detach immediately; resampling happens after.
    take: () => {
      const out = new Float32Array(len); let o = 0; chunks.forEach((c) => { out.set(c, o); o += c.length; });
      chunks = []; len = 0; paused = true;
      return out.length > 0.25 * sr ? resample(out, sr) : Promise.resolve(null);
    },
    stop: () => {
      if (stopped) return; stopped = true;
      proc.onaudioprocess = null; proc.disconnect();
      try { src.disconnect(proc); } catch { /* already detached */ }
      idleMic();
    },
  };
};

// Float32 @44.1k → 16-bit WAV blob (for Whisper upload / downloads).
export const wavBlob = (data, sr = 44100) => {
  const b = new ArrayBuffer(44 + data.length * 2), v = new DataView(b);
  const w = (o, s) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF'); v.setUint32(4, 36 + data.length * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, sr, true);
  v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); w(36, 'data'); v.setUint32(40, data.length * 2, true);
  let peak = 0; for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]));
  const g = peak > 1 ? 1 / peak : 1;
  for (let i = 0; i < data.length; i++) v.setInt16(44 + i * 2, Math.max(-1, Math.min(1, data[i] * g)) * 0x7fff, true);
  return new Blob([b], { type: 'audio/wav' });
};
